import { useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Copy, GripVertical, Trash2, Pencil, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { ImagePicker } from "@/components/instructor/ImagePicker";
import { getStorageUrl } from "@/lib/storage";
import { ContentStatusBadge, ContentStatusControls, type ContentStatus } from "@/components/lms/ContentStatusControls";
import type { Module } from "@/hooks/useCourses";

interface SortableModuleItemProps {
  module: Module;
  index: number;
  onDelete: (moduleId: string) => void;
  onUpdate?: (moduleId: string, title: string, description: string, thumbnail_url?: string | null, requiresHomework?: boolean) => void;
  onClone?: (moduleId: string) => void;
  onTogglePublish?: (moduleId: string, isPublished: boolean) => void;
  onChangeStatus?: (moduleId: string, next: ContentStatus) => void;
  isDeleting: boolean;
  isUpdating?: boolean;
  isCloning?: boolean;
  children: React.ReactNode;
}

export function SortableModuleItem({
  module,
  index,
  onDelete,
  onUpdate,
  onClone,
  onTogglePublish,
  onChangeStatus,
  isDeleting,
  isUpdating,
  isCloning,
  children,
}: SortableModuleItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(module.title);
  const [editDescription, setEditDescription] = useState(module.description || "");
  const [editThumbnail, setEditThumbnail] = useState<string | null>(module.thumbnail_url || null);
  // Opt-in homework gating (modules.requires_homework). Defaults to false, so
  // homework stays optional unless an instructor turns it on for this module.
  const [editRequiresHomework, setEditRequiresHomework] = useState<boolean>(
    (module as { requires_homework?: boolean }).requires_homework === true
  );

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: module.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleSave = () => {
    if (onUpdate && editTitle.trim()) {
      onUpdate(module.id, editTitle.trim(), editDescription.trim(), editThumbnail, editRequiresHomework);
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditTitle(module.title);
    setEditDescription(module.description || "");
    setEditThumbnail(module.thumbnail_url || null);
    setIsEditing(false);
  };

  return (
    <AccordionItem
      ref={setNodeRef}
      style={style}
      value={module.id}
      className={isDragging ? "bg-muted rounded-lg" : ""}
    >
      <AccordionTrigger className="hover:no-underline">
        <div className="flex items-center justify-between w-full pr-2">
          <div className="flex items-center gap-3 flex-1">
            <button
              type="button"
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing touch-none p-1 hover:bg-muted rounded"
              onClick={(e) => e.stopPropagation()}
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </button>
            {isEditing ? (
              <div className="flex items-center gap-2 flex-1" onClick={(e) => e.stopPropagation()}>
                <Input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="h-8 text-sm font-medium"
                  placeholder="Module title"
                  onClick={(e) => e.stopPropagation()}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSave();
                  }}
                  disabled={isUpdating || !editTitle.trim()}
                  className="h-7 w-7 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                >
                  <Check className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancel();
                  }}
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <>
                <span className="font-medium">
                  {index + 1}. {module.title}
                </span>
                <ContentStatusBadge
                  status={(module as any).status ?? (module.is_published ? "published" : "draft")}
                  className="ml-2"
                />

              </>
            )}
          </div>
          {!isEditing && (
            <div className="flex items-center gap-1">
              {onChangeStatus ? (
                <div className="mr-2" onClick={(e) => e.stopPropagation()}>
                  <ContentStatusControls
                    status={(module as any).status ?? (module.is_published ? "published" : "draft")}
                    onChange={(next) => onChangeStatus(module.id, next)}
                  />
                </div>
              ) : onTogglePublish && (
                <div className="flex items-center gap-1.5 mr-2" onClick={(e) => e.stopPropagation()}>
                  <Switch
                    checked={module.is_published}
                    onCheckedChange={(checked) => onTogglePublish(module.id, checked)}
                    className="scale-75"
                  />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {module.is_published ? "Published" : "Unpublished"}
                  </span>
                </div>
              )}
              {onClone && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClone(module.id);
                  }}
                  disabled={isCloning}
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                  title="Clone module"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              )}
              {onUpdate && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsEditing(true);
                  }}
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(module.id);
                }}
                disabled={isDeleting}
                className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </AccordionTrigger>
      <AccordionContent className="pl-10">
        <div className="space-y-4">
          {isEditing ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="Module description (optional)"
                  rows={2}
                  className="text-sm"
                />
              </div>
              <ImagePicker
                value={editThumbnail}
                onChange={setEditThumbnail}
                label="Module Featured Image"
              />
              <div className="flex items-start justify-between gap-4 rounded-md border p-3">
                <div className="space-y-0.5">
                  <p className="text-sm font-medium">Require homework to complete</p>
                  <p className="text-xs text-muted-foreground">
                    Off by default. When on, learners must submit homework for this
                    module before they can mark it complete.
                  </p>
                </div>
                <Switch
                  checked={editRequiresHomework}
                  onCheckedChange={setEditRequiresHomework}
                  aria-label="Require homework to complete this module"
                />
              </div>
            </div>
          ) : (
            <>
              {module.thumbnail_url && (
                <img
                  src={getStorageUrl("course-content", module.thumbnail_url) || undefined}
                  alt={module.title}
                  className="w-32 h-20 object-cover rounded-md border mb-2"
                />
              )}
              {module.description && (
                <p className="text-sm text-muted-foreground">
                  {module.description}
                </p>
              )}
            </>
          )}
          {children}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}